/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { PERMISSIONS } from '@kd/shared';
import { Badge, Button, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';

const PERM_LABEL: Record<string, string> = {
  'party.read': 'Клиенты: просмотр', 'party.write': 'Клиенты: изменение', 'party.delete': 'Клиенты: удаление', 'party.import': 'Клиенты: импорт', 'party.merge': 'Клиенты: слияние',
  'deal.read': 'Сделки: просмотр', 'deal.write': 'Сделки: изменение', 'deal.delete': 'Сделки: удаление',
  'contract.read': 'Договоры: просмотр', 'contract.write': 'Договоры: изменение', 'contract.delete': 'Договоры: удаление', 'contract.finance': 'Договоры: платежи',
  'expense.read': 'Затраты: просмотр', 'expense.write': 'Затраты: ввод', 'expense.approve': 'Затраты: согласование',
  'matter.read': 'Дела: просмотр', 'matter.write': 'Дела: изменение', 'matter.delete': 'Дела: удаление',
  'task.read': 'Задачи: просмотр', 'task.write': 'Задачи: изменение', 'document.read': 'Документы: просмотр', 'document.write': 'Документы: загрузка',
  'document.delete': 'Документы: удаление', 'report.read': 'Отчёты', 'dictionary.write': 'Справочники', 'user.manage': 'Пользователи и роли',
  'license.manage': 'Лицензия', 'audit.read': 'Журнал аудита', 'scope.all': 'Видит записи всех сотрудников',
};

export function UsersPage() {
  const [tab, setTab] = useState<'users' | 'roles'>('users');
  return (<><PageHeader title="Пользователи и роли" /><Tabs value={tab} onChange={setTab} tabs={[{ id: 'users', label: 'Пользователи' }, { id: 'roles', label: 'Роли' }]} />
    {tab === 'users' ? <UsersTab /> : <RolesTab />}</>);
}

function UsersTab() {
  const qc = useQueryClient();
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: () => api.get('/users') });
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles') });
  const [edit, setEdit] = useState<any>(null);
  if (!users || !roles) return <Spinner />;
  const roleName = (id: string) => roles.find((r: any) => r.id === id)?.name;
  return (
    <div className="card">
      <div className="flex justify-end border-b border-slate-200 p-2"><Button size="sm" onClick={() => setEdit({})}>Новый пользователь</Button></div>
      <table className="w-full">
        <thead className="bg-slate-50"><tr><th className="th">Сотрудник</th><th className="th">Роль</th><th className="th">2FA</th><th className="th">Последний вход</th><th className="th"></th></tr></thead>
        <tbody>{users.map((u: any) => (
          <tr key={u.id} className="border-t border-slate-100">
            <td className="td"><div className="font-medium">{u.fullName}</div><div className="text-xs text-slate-500">{u.email}</div></td>
            <td className="td">{roleName(u.roleId)}</td>
            <td className="td">{u.totpEnabled ? <Badge tone="green">включена</Badge> : <Badge>нет</Badge>}</td>
            <td className="td text-xs">{fmtDateTime(u.lastLoginAt)}</td>
            <td className="td text-right">{!u.isActive && <Badge tone="red">заблокирован</Badge>} <Button size="sm" variant="ghost" onClick={() => setEdit(u)}>Изменить</Button></td>
          </tr>
        ))}</tbody>
      </table>
      <Modal title={edit?.id ? 'Пользователь' : 'Новый пользователь'} open={!!edit} onClose={() => setEdit(null)}>
        {edit && <UserForm user={edit.id ? edit : undefined} roles={roles} onSaved={() => { setEdit(null); qc.invalidateQueries({ queryKey: ['users'] }); }} />}
      </Modal>
    </div>
  );
}

function UserForm({ user, roles, onSaved }: { user?: any; roles: any[]; onSaved: () => void }) {
  const [f, setF] = useState({ email: user?.email ?? '', fullName: user?.fullName ?? '', phone: user?.phone ?? '', roleId: user?.roleId ?? roles[0]?.id, password: '', isActive: user?.isActive ?? true });
  const save = useMutation({
    mutationFn: () => user
      ? api.patch(`/users/${user.id}`, { fullName: f.fullName, phone: f.phone || null, roleId: f.roleId, isActive: f.isActive, ...(f.password ? { password: f.password } : {}) })
      : api.post('/users', { email: f.email, fullName: f.fullName, phone: f.phone || undefined, roleId: f.roleId, password: f.password }),
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Почта (логин)"><Input type="email" required disabled={!!user} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <Field label="ФИО"><Input required value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Телефон"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="Роль"><Select value={f.roleId} onChange={(e) => setF({ ...f, roleId: e.target.value })} options={roles.map((r) => ({ value: r.id, label: r.name }))} /></Field>
      </div>
      <Field label={user ? 'Новый пароль' : 'Пароль'} hint={user ? 'Оставьте пустым, чтобы не менять' : 'Не короче 10 символов, буквы и цифры'}>
        <Input type="password" required={!user} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      </Field>
      {user && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.isActive} onChange={(e) => setF({ ...f, isActive: e.target.checked })} /> Активен</label>}
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>Сохранить</Button></div>
    </form>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles') });
  const [edit, setEdit] = useState<any>(null);
  const save = useMutation({
    mutationFn: (r: any) => (r.id ? api.patch(`/roles/${r.id}`, { name: r.name, permissions: r.permissions }) : api.post('/roles', r)),
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ['roles'] }); },
  });
  if (!roles) return <Spinner />;
  return (
    <div className="card">
      <div className="flex justify-end border-b border-slate-200 p-2"><Button size="sm" onClick={() => setEdit({ code: '', name: '', permissions: [] })}>Новая роль</Button></div>
      <table className="w-full"><tbody>{roles.map((r: any) => (
        <tr key={r.id} className="border-t border-slate-100">
          <td className="td font-medium">{r.name}{r.isSystem && <span className="ml-2 text-xs text-slate-400">системная</span>}</td>
          <td className="td text-xs text-slate-500">{r.permissions.length} прав</td>
          <td className="td text-right"><Button size="sm" variant="ghost" onClick={() => setEdit({ ...r })}>Права</Button></td>
        </tr>
      ))}</tbody></table>
      <Modal title={edit?.name || 'Новая роль'} open={!!edit} onClose={() => setEdit(null)} wide>
        {edit && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {!edit.id && <Field label="Код (латиница)"><Input value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} /></Field>}
              <Field label="Название"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            </div>
            <div className="grid gap-1 sm:grid-cols-2">
              {PERMISSIONS.map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={edit.permissions.includes(p)} disabled={edit.code === 'owner'}
                    onChange={(e) => setEdit({ ...edit, permissions: e.target.checked ? [...edit.permissions, p] : edit.permissions.filter((x: string) => x !== p) })} />
                  {PERM_LABEL[p] ?? p}
                </label>
              ))}
            </div>
            <ErrorText error={save.error} />
            <div className="flex justify-end"><Button onClick={() => save.mutate(edit)} disabled={edit.code === 'owner'}>Сохранить</Button></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
